import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Lightweight getUserMedia wrapper used by the desktop camera capture modal in
 * IdentityVerificationCard. We do NOT pull in `react-webcam` — the surface we
 * need is small and keeping it in-repo lets us match the editorial UI tokens
 * directly (status strings, error mapping for our own UX, mirror semantics).
 *
 * The hook is intentionally passive: it does NOT request camera access on mount.
 * Callers invoke `start()` from a user gesture (button click), which is required
 * by every browser's autoplay / permission policy. `stop()` is idempotent and
 * MUST be called on close to release the camera (otherwise the OS-level capture
 * indicator stays on, which users perceive as a security issue).
 */

export type CameraStatus =
	| 'idle'
	| 'requesting'
	| 'streaming'
	| 'denied'
	| 'no_device'
	| 'busy' // camera in use by another app
	| 'unsupported' // getUserMedia missing or running on non-secure context
	| 'error';

export type FacingMode = 'user' | 'environment';

export interface CameraDevice {
	deviceId: string;
	label: string;
}

export interface UseCameraReturn {
	stream: MediaStream | null;
	devices: CameraDevice[];
	activeDeviceId: string | null;
	status: CameraStatus;
	error: string | null;
	start: () => Promise<void>;
	stop: () => void;
	switchDevice: (deviceId: string) => Promise<void>;
	capturePhoto: (video: HTMLVideoElement, mirror: boolean) => Promise<Blob | null>;
}

interface UseCameraOptions {
	facingMode: FacingMode;
}

const isSecureContextOk = (): boolean => {
	if (typeof window === 'undefined') return false;
	// `getUserMedia` requires a secure context. localhost is treated as secure
	// by all evergreen browsers even on http://.
	return Boolean(window.isSecureContext) || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const hasMediaDevices = (): boolean =>
	typeof navigator !== 'undefined' && navigator.mediaDevices != null && typeof navigator.mediaDevices.getUserMedia === 'function';

const mapMediaError = (err: unknown): { status: CameraStatus; message: string } => {
	const name = (err as { name?: string })?.name ?? '';
	const fallback = (err as { message?: string })?.message ?? 'Camera access failed.';
	switch (name) {
		case 'NotAllowedError':
		case 'SecurityError':
			return {
				status: 'denied',
				message: 'We need permission to use your camera. Allow access in the browser address bar, then tap Try again.',
			};
		case 'NotFoundError':
		case 'OverconstrainedError':
			return { status: 'no_device', message: 'No camera was detected on this device.' };
		case 'NotReadableError':
		case 'TrackStartError':
			return {
				status: 'busy',
				message: 'Your camera is being used by another app. Close it (Zoom, Meet, FaceTime…) and try again.',
			};
		case 'AbortError':
			return { status: 'error', message: 'Camera start was interrupted. Try again.' };
		default:
			return { status: 'error', message: fallback };
	}
};

export const useCamera = ({ facingMode }: UseCameraOptions): UseCameraReturn => {
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [devices, setDevices] = useState<CameraDevice[]>([]);
	const [activeDeviceId, setActiveDeviceId] = useState<string | null>(null);
	const [status, setStatus] = useState<CameraStatus>('idle');
	const [error, setError] = useState<string | null>(null);

	// Hold the active stream in a ref too — needed inside `stop()` so it works
	// without depending on React state (which may be stale during unmount).
	const streamRef = useRef<MediaStream | null>(null);
	const mountedRef = useRef<boolean>(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const stopInternal = useCallback(() => {
		const current = streamRef.current;
		if (current) {
			current.getTracks().forEach((t) => t.stop());
		}
		streamRef.current = null;
		if (mountedRef.current) {
			setStream(null);
		}
	}, []);

	const stop = useCallback(() => {
		stopInternal();
		if (mountedRef.current) {
			setStatus('idle');
			setError(null);
		}
	}, [stopInternal]);

	const enumerate = useCallback(async (): Promise<CameraDevice[]> => {
		if (!hasMediaDevices() || !navigator.mediaDevices.enumerateDevices) return [];
		try {
			const all = await navigator.mediaDevices.enumerateDevices();
			const cams = all
				.filter((d) => d.kind === 'videoinput')
				.map((d, i) => ({
					deviceId: d.deviceId,
					label: d.label || `Camera ${i + 1}`,
				}));
			if (mountedRef.current) setDevices(cams);
			return cams;
		} catch {
			return [];
		}
	}, []);

	const requestStream = useCallback(async (constraints: MediaStreamConstraints): Promise<MediaStream> => {
		const next = await navigator.mediaDevices.getUserMedia(constraints);
		// Replace any existing stream — important for `switchDevice`.
		if (streamRef.current && streamRef.current !== next) {
			streamRef.current.getTracks().forEach((t) => t.stop());
		}
		streamRef.current = next;
		return next;
	}, []);

	const start = useCallback(async (): Promise<void> => {
		if (!hasMediaDevices()) {
			setStatus('unsupported');
			setError("This browser doesn't support in-page camera capture. Use the Upload option instead.");
			return;
		}
		if (!isSecureContextOk()) {
			setStatus('unsupported');
			setError('Camera capture requires a secure (https://) connection. Use the Upload option instead.');
			return;
		}

		setStatus('requesting');
		setError(null);

		const idealVideo: MediaTrackConstraints = {
			facingMode: { ideal: facingMode },
			width: { ideal: 1280 },
			height: { ideal: 960 },
		};

		try {
			let next: MediaStream;
			try {
				next = await requestStream({ video: idealVideo, audio: false });
			} catch (innerErr) {
				const name = (innerErr as { name?: string })?.name ?? '';
				// Many desktops have a single fixed camera — facingMode may be rejected
				// with OverconstrainedError. Retry with the default video device.
				if (name === 'OverconstrainedError' || name === 'NotFoundError') {
					next = await requestStream({ video: true, audio: false });
				} else {
					throw innerErr;
				}
			}

			if (!mountedRef.current) {
				next.getTracks().forEach((t) => t.stop());
				return;
			}

			setStream(next);
			setStatus('streaming');

			// Track the active device id from the first video track.
			const track = next.getVideoTracks()[0];
			const settings = track?.getSettings?.();
			if (settings?.deviceId) setActiveDeviceId(settings.deviceId);

			// Now that the user has granted permission, device labels become visible.
			await enumerate();
		} catch (err) {
			const mapped = mapMediaError(err);
			if (mountedRef.current) {
				setStatus(mapped.status);
				setError(mapped.message);
			}
		}
	}, [facingMode, requestStream, enumerate]);

	const switchDevice = useCallback(
		async (deviceId: string): Promise<void> => {
			if (!hasMediaDevices() || !deviceId) return;
			setStatus('requesting');
			setError(null);
			try {
				const next = await requestStream({
					video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 960 } },
					audio: false,
				});
				if (!mountedRef.current) {
					next.getTracks().forEach((t) => t.stop());
					return;
				}
				setStream(next);
				setActiveDeviceId(deviceId);
				setStatus('streaming');
			} catch (err) {
				const mapped = mapMediaError(err);
				if (mountedRef.current) {
					setStatus(mapped.status);
					setError(mapped.message);
				}
			}
		},
		[requestStream]
	);

	// Listen for hot-plug of cameras so the picker reflects reality.
	useEffect(() => {
		if (!hasMediaDevices()) return;
		const handler = () => {
			void enumerate();
		};
		navigator.mediaDevices.addEventListener?.('devicechange', handler);
		return () => navigator.mediaDevices.removeEventListener?.('devicechange', handler);
	}, [enumerate]);

	// Always release the camera if the host component unmounts mid-stream.
	useEffect(
		() => () => {
			stopInternal();
		},
		[stopInternal]
	);

	const capturePhoto = useCallback(
		(video: HTMLVideoElement, mirror: boolean): Promise<Blob | null> =>
			new Promise((resolve) => {
				// `videoWidth/Height` are the intrinsic frame dimensions, not the CSS
				// box. Captured photo is at the true sensor resolution.
				const w = video.videoWidth || 1280;
				const h = video.videoHeight || 960;
				const canvas = document.createElement('canvas');
				canvas.width = w;
				canvas.height = h;
				const ctx = canvas.getContext('2d');
				if (!ctx) {
					resolve(null);
					return;
				}
				if (mirror) {
					// Mirror preview only — we still want the saved photo to be the true
					// orientation. To get an UN-mirrored capture when the preview is
					// mirrored via CSS, draw the video frame normally (the CSS transform
					// is purely visual; the underlying video frames are un-mirrored).
					// Therefore: no canvas mirror needed here.
				}
				ctx.drawImage(video, 0, 0, w, h);
				canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
			}),
		[]
	);

	return useMemo(
		() => ({
			stream,
			devices,
			activeDeviceId,
			status,
			error,
			start,
			stop,
			switchDevice,
			capturePhoto,
		}),
		[stream, devices, activeDeviceId, status, error, start, stop, switchDevice, capturePhoto]
	);
};

export default useCamera;
